import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Icon } from "./ui/UIElements/Icon";
import { useLogout } from "@finitless/shared/hooks";
import {
  useToastContext,
  useWebsocket,
  useBrand,
} from "@finitless/shared/contexts";
import { useUserStore, useThemeState } from "@finitless/shared/stores";
import { useOrderNotifications } from "../hooks/useOrderNotifications";
import {
  useBilling,
  useBillingRedirect,
  useIsProjectOwner,
} from "../hooks/useBilling";
import { useBillingStore } from "../stores/billingStore";
import {
  BusinessDropdown,
  LiveIndicator,
  LocaleSelector,
  BillingStatusIndicator,
  TrialExtensionTopBarAction,
} from "./ui";
import { Button, IconButton } from "./ui/UIElements/Button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/UIElements/Popover";
import { useTranslation } from "react-i18next";
import { useUiLocale } from "../hooks/useUiLocale";
// ProductSwitcher removed - Connect consolidated into Dashboard

const SUPPORT_BASE_URL = "https://docs.ordering.co/docs/products/ai-dashboard/";

const LEARN_PAGE_MAP = {
  "/overview": { labelKey: "sidebar.overview", path: "start-here/quickstart/" },
  "/ai-agents": {
    labelKey: "sidebar.aiAgents",
    path: "build-your-ai-agent/creating-a-finitless-ai-agent/",
  },
  "/orders": {
    labelKey: "sidebar.orders",
    path: "orders-stores-operations/ai-orders-dashboard/",
  },
  "/users": {
    labelKey: "sidebar.customers",
    path: "settings-users-billing/customers/",
  },
  "/analyze/conversations": {
    labelKey: "sidebar.conversations",
    path: "build-your-ai-agent/analyze-conversations/",
  },
  "/analyze/voice-calls": {
    labelKey: "sidebar.voiceCalls",
    path: "build-your-ai-agent/analyze-conversations/",
  },
  "/analyze/reports": {
    labelKey: "sidebar.reports",
    path: "orders-stores-operations/analyze-reports/",
  },
  "/menu/stores": {
    labelKey: "sidebar.stores",
    path: "orders-stores-operations/stores-module/",
  },
  "/menu/products": {
    labelKey: "sidebar.products",
    path: "orders-stores-operations/products-and-creating-a-menu/",
  },
  "/menu/brands": {
    labelKey: "sidebar.brands",
    path: "orders-stores-operations/brands/",
  },
  "/growth/cart-recovery": {
    labelKey: "sidebar.cartRecovery",
    path: "growth-promotions/campaigns/",
  },
  "/campaigns": {
    labelKey: "sidebar.campaigns",
    path: "growth-promotions/campaigns/",
  },
  "/discounts": {
    labelKey: "sidebar.discounts",
    path: "growth-promotions/discounts/",
  },
  "/growth/link-in-bio": {
    labelKey: "sidebar.linkInBio",
    path: "",
  },
  "/integrations/pos": {
    labelKey: "sidebar.posSystems",
    path: "integrations-channels/setup-pos-systems/",
  },
  "/integrations/menu-importers": {
    labelKey: "sidebar.menuImporters",
    path: "integrations-channels/menu-importers/",
  },
  "/integrations/sales-channels": {
    labelKey: "sidebar.salesChannels",
    path: "integrations-channels/setup-sales-channels/",
  },
  "/integrations/delivery-services": {
    labelKey: "sidebar.deliveryServices",
    path: "delivery/setup-delivery/",
  },
  "/delivery/drivers-managers": {
    labelKey: "sidebar.driversManagers",
    path: "delivery/delivery-driver-manager/",
  },
  "/delivery/groups": {
    labelKey: "sidebar.deliveryGroups",
    path: "delivery/delivery-groups/",
  },
  "/settings/basic": {
    labelKey: "sidebar.general",
    path: "settings-users-billing/settings-general/",
  },
  "/settings/online-payments": {
    labelKey: "sidebar.onlinePayments",
    path: "settings-users-billing/online-payments/",
  },
  "/settings/agent-translations": {
    labelKey: "sidebar.agentTranslations",
    path: "settings-users-billing/agent-translations/",
  },
  "/settings/admins": {
    labelKey: "sidebar.admins",
    path: "settings-users-billing/admins/",
  },
  "/support": {
    labelKey: "sidebar.support",
    path: "account-troubleshooting/creating-a-support-ticket/",
  },
};

const getCurrentPageInfo = (pathname) => {
  const sortedKeys = Object.keys(LEARN_PAGE_MAP).sort(
    (a, b) => b.length - a.length,
  );
  for (const key of sortedKeys) {
    if (pathname === key || pathname.startsWith(key + "/")) {
      return LEARN_PAGE_MAP[key];
    }
  }
  return null;
};

const TopBar = ({
  onToggleSidebar,
  onToggleMobileSidebar,
  mobileSidebarTriggerRef,
  isMobileSidebarOpen = false,
  mobileSidebarId,
  isSidebarCollapsed = false,
}) => {
  const { t } = useTranslation(["app", "navigation", "toasts", "billing"]);
  const { locale, setLocale, supportedLocales } = useUiLocale();
  const location = useLocation();
  const [isBrandDropdownOpen, setIsBrandDropdownOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [isLearnDropdownOpen, setIsLearnDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const currentPageInfo = getCurrentPageInfo(location.pathname);
  const logoutMutation = useLogout();
  const toast = useToastContext();
  const { user } = useUserStore();
  const { theme, setTheme } = useThemeState();
  const {
    isConnected,
    connectionState,
    reconnectAttempt,
    maxReconnectAttempts,
  } = useWebsocket();
  const { isEnabled: notificationsEnabled, toggleNotifications } =
    useOrderNotifications();

  const { selectedBrand, handleBrandChange, state } = useBrand();
  const { redirectToBillingPortal, isRedirecting } = useBillingRedirect();
  const { upgradePlanId } = useBilling();
  const setShowUpgradeModal = useBillingStore((s) => s.setShowUpgradeModal);

  const hideThemeToggle = false;

  const isProjectOwner = useIsProjectOwner();
  const showUpgradeToPro = isProjectOwner && !!upgradePlanId;

  // Show brand selector only on specific pages
  const shouldShowBrandSelector = false;
  const handleBrandSelect = (brand) => {
    handleBrandChange(brand);
    setIsBrandDropdownOpen(false);
  };

  const handleLogout = async () => {
    try {
      await logoutMutation.mutateAsync();
      toast.success(t("auth.logoutSuccess", { ns: "toasts" }));
      setIsUserDropdownOpen(false);
    } catch {
      toast.error(t("auth.logoutFailed", { ns: "toasts" }));
    }
  };

  const handleThemeToggle = () => {
    const newTheme = theme === "dark" ? "light" : "dark";
    setTheme(newTheme);
  };

  const themeToggleLabel =
    theme === "dark"
      ? t("topbar.switchToLight", { ns: "navigation" })
      : t("topbar.switchToDark", { ns: "navigation" });

  const handleMyAccountClick = () => {
    navigate("/account");
    setIsUserDropdownOpen(false);
  };

  const handleLocaleChange = async (event) => {
    await setLocale(event.target.value);
  };

  // Cerrar dropdowns cuando se hace clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsBrandDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <header className="ordering-shell__topbar flex h-16 items-center justify-between border-b px-2 sm:px-4">
      {/* Left Side */}
      <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
        {/* Mobile Menu Button */}
        <IconButton
          ref={mobileSidebarTriggerRef}
          type="button"
          onClick={onToggleMobileSidebar}
          emphasis="ghost"
          intent="neutral"
          size="iconTouch"
          className="ordering-shell__control hover:bg-accent min-h-11 min-w-11 flex-shrink-0 p-1.5 lg:hidden"
          data-shell-action="toggle-mobile-sidebar"
          aria-haspopup="dialog"
          aria-expanded={isMobileSidebarOpen}
          aria-controls={mobileSidebarId}
          aria-label={
            isMobileSidebarOpen
              ? t("sidebar.closeSidebar", { ns: "navigation" })
              : t("topbar.openNavigation", { ns: "navigation" })
          }
        >
          <Icon name="Menu" className="text-foreground h-5 w-5" />
        </IconButton>

        {/* Brand Switcher - Only shown on overview, orders, and conversations */}
        {shouldShowBrandSelector && (
          <div className="flex items-center gap-1 sm:gap-2">
            <IconButton
              type="button"
              onClick={onToggleSidebar}
              emphasis="ghost"
              intent="neutral"
              size="icon"
              className="ordering-shell__control hover:bg-accent hidden h-8 w-8 flex-shrink-0 p-1 lg:block"
              data-shell-action="toggle-desktop-sidebar"
              aria-label={
                isSidebarCollapsed
                  ? t("topbar.expandNavigation", { ns: "navigation" })
                  : t("topbar.collapseNavigation", { ns: "navigation" })
              }
            >
              <Icon name="Menu" className="text-muted-foreground h-4 w-4" />
            </IconButton>

            <div className="relative min-w-0 flex-1" ref={dropdownRef}>
              <Button
                type="button"
                onClick={() => {
                  if (state === "error" || state === "empty") return;
                  setIsBrandDropdownOpen(!isBrandDropdownOpen);
                }}
                disabled={
                  state === "loading" || state === "error" || state === "empty"
                }
                variant="ghost"
                size={null}
                className={`ordering-shell__control flex min-h-8 w-full min-w-0 items-center gap-1 px-2 py-1.5 sm:gap-2 ${
                  isBrandDropdownOpen ? "bg-accent" : "hover:bg-accent"
                } ${
                  state === "loading" || state === "error" || state === "empty"
                    ? "cursor-not-allowed opacity-50"
                    : ""
                }`}
              >
                {state === "loading" ? (
                  <>
                    <div className="border-muted-foreground h-4 w-4 flex-shrink-0 animate-spin rounded-full border-b-2"></div>
                    <span className="text-muted-foreground truncate text-xs sm:text-sm">
                      {t("topbar.loading", { ns: "navigation" })}
                    </span>
                  </>
                ) : state === "error" ? (
                  <>
                    <Icon
                      name="AlertCircle"
                      className="text-error h-4 w-4 flex-shrink-0"
                    />
                    <span className="text-error truncate text-xs sm:text-sm">
                      {t("topbar.failed", { ns: "navigation" })}
                    </span>
                  </>
                ) : state === "empty" ? (
                  <>
                    <Icon
                      name="Building"
                      className="text-muted-foreground h-4 w-4 flex-shrink-0"
                    />
                    <span className="text-muted-foreground truncate text-xs sm:text-sm">
                      {t("topbar.noBusinesses", { ns: "navigation" })}
                    </span>
                  </>
                ) : selectedBrand ? (
                  <>
                    <span className="text-foreground max-w-32 truncate text-xs font-normal sm:text-sm lg:max-w-48">
                      {selectedBrand.name}
                    </span>
                    <Icon
                      name="ChevronDown"
                      className="text-muted-foreground h-4 w-4 flex-shrink-0"
                    />
                  </>
                ) : (
                  <>
                    <span className="text-muted-foreground truncate text-xs sm:text-sm">
                      {t("topbar.selectBusiness", { ns: "navigation" })}
                    </span>
                    <Icon
                      name="ChevronDown"
                      className="text-muted-foreground h-4 w-4 flex-shrink-0"
                    />
                  </>
                )}
              </Button>

              <BusinessDropdown
                isOpen={isBrandDropdownOpen}
                onClose={() => setIsBrandDropdownOpen(false)}
                selectedBrand={selectedBrand}
                onBrandSelect={handleBrandSelect}
              />
            </div>

            {state === "success" && selectedBrand?.address && (
              <>
                <div className="text-muted-foreground hidden sm:block">|</div>
                {/* Address - Hidden on mobile to save space */}
                <span className="text-muted-foreground hidden max-w-32 truncate text-xs sm:text-sm lg:max-w-48 xl:block">
                  {selectedBrand.address}
                </span>
              </>
            )}
          </div>
        )}

        {/* Show only the sidebar toggle for pages without brand selector */}
        {!shouldShowBrandSelector && (
          <IconButton
            type="button"
            onClick={onToggleSidebar}
            emphasis="ghost"
            intent="neutral"
            size="icon"
            className="ordering-shell__control hover:bg-accent hidden h-8 w-8 flex-shrink-0 p-1 lg:block"
            data-shell-action="toggle-desktop-sidebar"
            aria-label={
              isSidebarCollapsed
                ? t("topbar.expandNavigation", { ns: "navigation" })
                : t("topbar.collapseNavigation", { ns: "navigation" })
            }
          >
            <Icon name="Menu" className="text-muted-foreground h-4 w-4" />
          </IconButton>
        )}
      </div>

      {/* Right Side */}
      <div className="flex flex-shrink-0 items-center gap-1 sm:gap-2">
        {/* Billing status indicator */}
        <div className="hidden xl:block">
          <BillingStatusIndicator />
        </div>

        {/* Trial extension offer - persistent entry point while available */}
        <TrialExtensionTopBarAction />

        {/* Upgrade to Pro - only when plan has upgrade_plan_id */}
        {showUpgradeToPro && (
          <Button
            type="button"
            onClick={() => setShowUpgradeModal(true)}
            variant="ghost"
            size={null}
            className="ordering-shell__control bg-warning-bg text-warning flex min-h-8 items-center gap-1 px-2 py-1.5 hover:brightness-95 sm:gap-2"
            aria-label={t("upgradeToPro", { ns: "billing" })}
          >
            <Icon name="Star" className="text-warning h-4 w-4 flex-shrink-0" />
            <span className="hidden text-[13px] font-medium sm:inline">
              {t("upgradeToPro", { ns: "billing" })}
            </span>
          </Button>
        )}

        {/* Manage Billing - only for project owners */}
        {isProjectOwner && (
          <Button
            type="button"
            onClick={() =>
              redirectToBillingPortal().catch((err) =>
                toast.error(
                  err?.message || t("redirectFailed", { ns: "billing" }),
                ),
              )
            }
            disabled={isRedirecting}
            variant="ghost"
            size={null}
            className="ordering-shell__control hover:bg-accent flex min-h-8 items-center gap-1 px-2 py-1.5 disabled:cursor-not-allowed disabled:opacity-50 sm:gap-2"
            aria-label={t("manageBilling", { ns: "billing" })}
          >
            {isRedirecting ? (
              <div className="border-muted-foreground h-4 w-4 animate-spin rounded-full border-b-2"></div>
            ) : (
              <Icon
                name="CreditCard"
                className="text-foreground h-4 w-4 flex-shrink-0"
              />
            )}
            <span className="text-foreground hidden text-[13px] font-normal sm:inline">
              {t("manageBilling", { ns: "billing" })}
            </span>
          </Button>
        )}

        {/* Learn dropdown - only shown when current route is in LEARN_PAGE_MAP */}
        {currentPageInfo && (
          <Popover
            open={isLearnDropdownOpen}
            onOpenChange={setIsLearnDropdownOpen}
          >
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size={null}
                className={`ordering-shell__control flex min-h-8 items-center gap-1 px-2 py-1.5 sm:gap-2 ${
                  isLearnDropdownOpen ? "bg-accent" : "hover:bg-accent"
                }`}
                aria-label={t("topbar.learn", { ns: "navigation" })}
              >
                <Icon
                  name="BookOpen"
                  className="text-foreground h-4 w-4 flex-shrink-0"
                />
                <span className="text-foreground hidden text-[13px] font-normal sm:inline">
                  {t("topbar.learn", { ns: "navigation" })}
                </span>
                <Icon
                  name="ChevronDown"
                  className="text-muted-foreground h-4 w-4 flex-shrink-0"
                />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              align="end"
              aria-label={t("topbar.learnMenu", { ns: "navigation" })}
              className="ordering-shell__popover w-56 p-0"
            >
              <div className="py-2">
                <a
                  href={`${SUPPORT_BASE_URL}${currentPageInfo.path}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ordering-shell__control hover:bg-accent text-foreground flex min-h-11 w-full items-center gap-3 px-3 py-2 text-start text-[13px]"
                  onClick={() => setIsLearnDropdownOpen(false)}
                >
                  <Icon name="BookOpen" className="h-4 w-4 flex-shrink-0" />
                  <span>
                    {t("topbar.learnAboutPage", {
                      ns: "navigation",
                      page: t(currentPageInfo.labelKey, { ns: "navigation" }),
                    })}
                  </span>
                </a>
              </div>
            </PopoverContent>
          </Popover>
        )}

        <LocaleSelector
          id="ui-locale-selector"
          label={t("language.label", { ns: "app" })}
          showLabel={false}
          showLeadingIcon={false}
          value={locale}
          onChange={handleLocaleChange}
          options={supportedLocales}
          containerClassName="hidden lg:block"
          selectClassName="ordering-shell__locale-control h-8 min-w-[108px] border-border bg-background py-1 text-[13px]"
        />

        <div className="hidden xl:block">
          <Button
            type="button"
            onClick={toggleNotifications}
            variant="ghost"
            size={null}
            className={`ordering-shell__control flex min-h-8 items-center justify-center p-1.5 sm:px-2 sm:py-1.5 ${
              notificationsEnabled
                ? "bg-[var(--od-success-signal,var(--success))] text-[var(--od-ink-950,#0b0c0d)] hover:brightness-95"
                : "bg-secondary text-secondary-foreground hover:bg-accent"
            }`}
            title={
              notificationsEnabled
                ? t("topbar.disableNotifications", { ns: "navigation" })
                : t("topbar.enableNotifications", { ns: "navigation" })
            }
            aria-label={
              notificationsEnabled
                ? t("topbar.disableNotifications", { ns: "navigation" })
                : t("topbar.enableNotifications", { ns: "navigation" })
            }
            aria-pressed={notificationsEnabled}
          >
            <Icon
              name={notificationsEnabled ? "Bell" : "BellOff"}
              className="h-4 w-4 sm:h-4 sm:w-4"
            />
            <span className="ms-1 hidden text-xs lg:inline">
              {notificationsEnabled
                ? t("common.on", { ns: "app" })
                : t("common.off", { ns: "app" })}
            </span>
          </Button>
        </div>

        {!hideThemeToggle && (
          <div className="flex-shrink-0">
            <IconButton
              type="button"
              onClick={handleThemeToggle}
              emphasis="ghost"
              intent="neutral"
              size="iconTouch"
              className="ordering-shell__control hover:bg-accent min-h-8 min-w-8 flex-shrink-0 p-1.5"
              title={themeToggleLabel}
              aria-label={themeToggleLabel}
            >
              {theme === "dark" ? (
                <Icon
                  name="Sun"
                  className="text-warning h-4 w-4 sm:h-5 sm:w-5"
                />
              ) : (
                <Icon
                  name="Moon"
                  className="text-muted-foreground h-4 w-4 sm:h-5 sm:w-5"
                />
              )}
            </IconButton>
          </div>
        )}
        <div className="hidden xl:block">
          <LiveIndicator
            isConnected={isConnected}
            connectionState={connectionState}
            reconnectAttempt={reconnectAttempt}
            maxReconnectAttempts={maxReconnectAttempts}
          />
        </div>

        <Popover open={isUserDropdownOpen} onOpenChange={setIsUserDropdownOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size={null}
              className={`ordering-shell__control flex min-h-8 items-center gap-1 p-1 sm:gap-2 sm:p-1.5 ${
                isUserDropdownOpen ? "bg-accent" : "hover:bg-accent"
              }`}
              aria-label={t("topbar.userMenu", { ns: "navigation" })}
            >
              <div className="bg-primary flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full sm:h-8 sm:w-8">
                {user.photo ? (
                  <img
                    src={user.photo}
                    alt={t("common.user", { ns: "app" })}
                    className="h-7 w-7 rounded-full object-cover sm:h-8 sm:w-8"
                  />
                ) : (
                  <Icon
                    name="User"
                    className="h-3.5 w-3.5 text-white sm:h-4 sm:w-4"
                  />
                )}
              </div>
              <div className="hidden min-w-0 text-start md:block">
                <div className="text-foreground max-w-24 truncate text-[13px] font-medium lg:max-w-32">
                  {user.name || user.email || t("common.user", { ns: "app" })}
                </div>
                <div className="text-muted-foreground max-w-24 truncate text-xs lg:max-w-32">
                  {user.role || t("common.user", { ns: "app" })}
                </div>
              </div>
              <Icon
                name="ChevronDown"
                className="text-muted-foreground hidden h-3 w-3 flex-shrink-0 sm:block sm:h-4 sm:w-4"
              />
            </Button>
          </PopoverTrigger>

          {/* User Dropdown */}
          <PopoverContent
            align="end"
            aria-label={t("topbar.userMenu", { ns: "navigation" })}
            className="ordering-shell__popover w-56 p-0"
          >
            <div className="py-2">
              <div className="border-border border-b px-3 py-2">
                <div className="text-foreground truncate font-normal">
                  {user.name || t("common.user", { ns: "app" })}
                </div>
                <div className="text-muted-foreground truncate text-sm">
                  {user.email}
                </div>
              </div>

              <div className="border-border border-b px-3 py-2 md:hidden">
                <LocaleSelector
                  id="ui-locale-selector-mobile"
                  label={t("language.label", { ns: "app" })}
                  showLabel={false}
                  showLeadingIcon={false}
                  value={locale}
                  onChange={handleLocaleChange}
                  options={supportedLocales}
                  containerClassName="w-full"
                  selectClassName="ordering-shell__locale-control h-8 min-w-0 w-full border-border bg-background py-1 text-[13px]"
                />
              </div>

              <Button
                type="button"
                onClick={handleMyAccountClick}
                variant="ghost"
                size={null}
                className="ordering-shell__control hover:bg-accent text-muted-foreground hover:text-foreground min-h-11 w-full cursor-pointer px-3 py-2 text-start"
              >
                <div className="flex items-center gap-3">
                  <Icon name="User" className="h-4 w-4" />
                  <span>{t("topbar.account", { ns: "navigation" })}</span>
                </div>
              </Button>

              <div className="border-border my-1 border-t"></div>

              <Button
                type="button"
                onClick={handleLogout}
                disabled={logoutMutation.isPending}
                emphasis="ghost"
                intent="danger"
                size={null}
                className="ordering-shell__control hover:bg-error-bg text-error hover:text-error disabled:hover:text-error min-h-11 w-full cursor-pointer px-3 py-2 text-start disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent"
              >
                <div className="flex items-center gap-3">
                  {logoutMutation.isPending ? (
                    <div className="border-error h-4 w-4 animate-spin rounded-full border-b-2"></div>
                  ) : (
                    <Icon name="LogOut" className="h-4 w-4" />
                  )}
                  <span>
                    {logoutMutation.isPending
                      ? t("topbar.loggingOut", { ns: "navigation" })
                      : t("topbar.logout", { ns: "navigation" })}
                  </span>
                </div>
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </header>
  );
};

export default TopBar;
